import { DateTime } from "luxon";
import { Attack, Graph } from "../types";

const MONTHS_IN_YEAR = 12;
const SHORTENED_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const toMonthYearPair = (datetime: DateTime) => [datetime.month, datetime.year];

const toShortMonth = (monthNumber: number) => {
  if (monthNumber < 1 || monthNumber > 12) {
    return "Invalid Month";
  }

  return SHORTENED_MONTHS[monthNumber - 1];
};

export const collateGraphData = (graph: Graph, attacks: Attack[]) => {
  switch (graph) {
    case "migrainesPerMonth":
      return collateMigrainesByMonth(attacks);
    case "nonMigrainesPerMonth":
      return collateNonMigrainesByMonth(attacks);
    default:
      return collateAttacksByMonth(attacks);
  }
};

const collateAttacksByMonth = (attacks: Attack[]) => {
  // Use [Map] to preserve order of keys
  const attacksByMonth = new Map<string, number>();
  if (attacks.length === 0) return attacksByMonth;

  const startDateTimes = attacks
    .map((attack) => DateTime.fromISO(attack.startDateTime))
    .sort();

  const minMonth = toMonthYearPair(startDateTimes[0]);
  const maxMonth = toMonthYearPair(startDateTimes[startDateTimes.length - 1]);

  // Initialise map
  let [currMonth, currYear] = minMonth;
  while (currMonth !== maxMonth[0] || currYear !== maxMonth[1]) {
    const key = `${toShortMonth(currMonth)} ${currYear}`;
    attacksByMonth.set(key, 0);

    ++currMonth;
    if (currMonth === MONTHS_IN_YEAR + 1) {
      currMonth = 1;
      ++currYear;
    }
  }
  const key = `${toShortMonth(maxMonth[0])} ${maxMonth[1]}`;
  attacksByMonth.set(key, 0);

  // Populate map
  attacks.forEach((attack) => {
    const datetime = DateTime.fromISO(attack.startDateTime);
    // Do not use [datetime.monthShort] since it is inconsistent with September
    // - it either returns Sept or Sep depending on locality
    const month = toShortMonth(datetime.month);
    const year = datetime.year;
    const key = `${month} ${year}`;
    const currCount = attacksByMonth.get(key) || 0;
    attacksByMonth.set(key, currCount + 1);
  });

  return attacksByMonth;
};

const collateMigrainesByMonth = (attacks: Attack[]) => {
  const migraines = attacks.filter(
    (attack) => attack.suspectedHeadacheType === "migraine",
  );
  return collateAttacksByMonth(migraines);
};

const collateNonMigrainesByMonth = (attacks: Attack[]) => {
  const nonMigraines = attacks.filter(
    (attack) => attack.suspectedHeadacheType !== "migraine",
  );
  return collateAttacksByMonth(nonMigraines);
};

export const toGraphLabel = (graph: Graph) => {
  switch (graph) {
    case "migrainesPerMonth":
      return "Migraines";
    case "nonMigrainesPerMonth":
      return "Non-migraines";
    default:
      return "Headache attacks";
  }
};
